<style lang="sass" scoped>
  @import '../../../sass/variables'

  #card-title
    font-weight: 700
    font-size: 1.5rem
    line-height: 130%
    color: $salesflow-font-gray

  .card-body
    min-height: 600px
    margin-bottom: 10px

    #funnel
      height: 600px

  ::v-deep
    .svg-funnel-js
      font-family: $font-family-sans-serif
      font-weight: 300

      &:not(.svg-funnel-js--vertical)
        .svg-funnel-js__label
          border-left: 0 !important
          border-right: 1px solid $gray-white
          height: 565px

      .svg-funnel-js__labels
        .svg-funnel-js__label
          &:hover
            .label__segment-percentages
              opacity: 1
              border-radius: 15px
              width: 50%
              left: 25%
              top: 40%
              transition: opacity 300ms ease

          .label__title
            color: $salesflow-font-gray
            font-size: 1rem
            font-weight: 400
            line-height: 130%

          .label__value
            color: $salesflow-font-dark
            font-size: 2rem
            font-weight: 500
            line-height: 130%
            margin: 0
            padding: 0

          .label__percentage
            font-weight: 400
            font-size: 1rem
            color: $salesflow-font-gray
            line-height: 130%

      .svg-funnel-js__subLabel--title
        color: $black
        font-weight: 300

      .svg-funnel-js__subLabels
        display: none

  .deals
    margin-left: -30px

    h4
      color: $black
      font-size: 0.875rem
      font-weight: 300

  .details
    margin-top: -200px
</style>

<template>
  <div
    class="card with-shadow"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
  >
    <div class="card-header">
      <div class="row">
        <div class="col-12 d-flex">
          <h2 id="card-title">{{ __('Visão geral do seu negócio') }}<small class="ml-1" /></h2>
          <div class="ml-auto mr-3">
            <GradientIcon
              class="mr-1"
              size="10"
              :colors="['#FF2E56', '#FF2E56']"
            /><span class="small font-weight-bold align-middle mr-2">{{ __('PAGO') }}</span>
            <GradientIcon
              class="mr-1"
              size="10"
              :colors="['#7E5DF4', '#7E5DF4']"
            /><span class="small font-weight-bold align-middle mr-2">{{ __('ORGÂNICO') }}</span>
            <GradientIcon
              class="mr-1"
              size="10"
              :colors="['#2CEBA9', '#2CEBA9']"
            /><span class="small font-weight-bold align-middle">{{ __('DIRETO') }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      class="card-body"
      id="funnel-card"
      :class="{ hovering }"
    >
      <div
        class="loader"
        v-if="loading"
      >
        <h2 class="text-center">
          <i class="fa fa-circle-o-notch fa-spin" />
        </h2>
      </div>
      <DashboardFakeData
        :hovering="hovering"
        cta="SAIBA MAIS SOBRE A VISÃO GERAL"
        href="https://salesflowcrm.movidesk.com/kb/article/406298/dashboard-visao-geral"
        content="Integre seu site, converta leads, crie negócios e tenha o controle total de sua empresa em um único lugar."
        v-else-if="fake"
      />
      <DashboardEmptyData v-else-if="empty" />
      <div
        class="data"
        :class="{ disabled: loading, fake, hovering, hidden: empty }"
        v-if="!empty"
      >
        <div class="row">
          <div class="col-10">
            <div id="funnel">
              <VueFunnelGraph
                v-if="!loading && !empty && funnel.values.length > 0"
                :width="funnel.width"
                :height="funnel.height"
                :labels="labels"
                :values="funnel.values"
                :colors="funnel.colors"
                :sub-labels="subLabels"
                :direction="funnel.direction"
                :gradient-direction="funnel.gradientDirection"
                :animated="funnel.animated"
                :display-percentage="funnel.percentage"
              />
            </div>
          </div>
          <div class="col-2">
            <div class="deals">
              <h4>{{ __('Negócios') }}</h4>
              <div v-if="shouldDisplayDashboard()">
                <div
                  v-if="data.radials && data.radials.length > 0"
                  class="radial"
                  v-for="radial in data.radials"
                  :key="radial.title"
                >
                  <RadialChart
                    :title="__(radial.title)"
                    :value="radial.value"
                    :percent="radial.percent"
                    :colors="radial.colors"
                    :large="radial.large || false"
                  />
                </div>
              </div>
              <div v-else>
                <p>{{ __('Nenhum dado foi encontrado! Já tentou alterar as datas?') }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="details">
          <DashboardTop5Widget
            :user="user"
            :range="range"
            :imports="imports"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { VueFunnelGraph } from 'vue-funnel-graph-js';
import RadialChart from './RadialChart';
import GradientIcon from './GradientIcon';
import Widget from '../../mixins/widget';

export default {
  props: ['user', 'range', 'imports'],

  mixins: [Widget],

  components: {
    RadialChart,
    GradientIcon,
    VueFunnelGraph,
  },

  mounted() {
    this.initializeRange();
    this.$nextTick(() => {
      this.calculateFunnelWidth();
      this.getDashboardData();
    });
  },

  data() {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - 15);

    return {
      name: 'funnel',
      loading: true,
      fake: false,
      empty: false,
      hovering: false,
      data: {},
      funnel: {
        labels: [],
        subLabels: [],
        values: [],
        colors: [],
        direction: 'horizontal',
        gradientDirection: 'horizontal',
        height: 300,
        width: 0,
        animated: true,
        percentage: true,
      },
      localRange: this.range || `${this.formatDate(startDate)} > ${this.formatDate(today)}`,
    };
  },

  watch: {
    loading(newValue) {
      if (!newValue) {
        this.empty = this.dataIsEmpty();
      }
    },

    range: {
      handler(newRange) {
        this.localRange = newRange || this.localRange;
        this.getDashboardData();
      },
      immediate: true,
    },

    user: {
      handler() {
        this.getDashboardData();
      },
      immediate: true,
    },

    data: {
      handler(newData, oldData) {
        if (!this.hasDataChanged(newData, oldData)) {
          this.empty = this.dataIsEmpty();
          return;
        }
        this.empty = this.dataIsEmpty();
      },
      deep: true,
    },
  },

  computed: {
    labels() {
      return this.funnel.labels.map((item) => (typeof item === 'string' ? this.__(item) : ''));
    },

    subLabels() {
      return this.funnel.subLabels.map((item) => (typeof item === 'string' ? this.__(item) : ''));
    },
  },

  methods: {
    formatDate(date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    initializeRange() {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 15);
      this.localRange = `${this.formatDate(startDate)} > ${this.formatDate(today)}`;
    },

    async fetchData(endpoint, range) {
      const url = `${endpoint}?range=${encodeURIComponent(range)}&imports=1`;
      try {
        const response = await fetch(url);
        if (!response.ok) {
          return { error: true };
        }
        return response.json();
      } catch {
        return { error: true };
      }
    },

    async getDashboardData() {
      this.loading = true;

      try {
          if (!this.localRange) {
              this.initializeRange();
          }

          const [funnelResponse, top5Response] = await Promise.all([
              this.fetchData('/widget/funnel', this.localRange),
              this.fetchData('/widget/top5', this.localRange),
          ]);

          if (!funnelResponse?.error && funnelResponse.values?.length > 0) {
              this.updateFunnelData(funnelResponse);
          } else {
              this.funnel.values = [];
          }

          if (!top5Response?.error && top5Response.length > 0) {
              this.updateTop5Data(top5Response);
          }

          this.empty = this.dataIsEmpty();
      } catch {
          this.empty = true;
      } finally {
          this.loading = false;
      }
    },

    validateFunnelData(funnelResponse) {
      return (
        funnelResponse.values &&
        funnelResponse.values.some((group) => group.some((value) => value > 0))
      );
    },

    updateFunnelData(funnelResponse) {
      if (!this.validateFunnelData(funnelResponse)) {
        if (this.top5Data && this.top5Data.length > 0) {
          const allActivitiesAreNotes = this.top5Data.some((widget) =>
            widget.title === 'As 5 atividades mais executadas' &&
            widget.data.every((activity) => activity.name === 'Nota')
          );

          if (allActivitiesAreNotes) {
            this.funnel.values = [];
            this.empty = true;
            return;
          }
          this.funnel = {
            ...this.funnel,
            labels: ['Visitantes', 'Leads', 'Atendidos', 'Clientes'],
            values: [[0, 0, 0], [0, 0, 0], [0, 0, 0], [0, 0, 0]],
            colors: [['#FF8EB0', '#F31357'], ['#D5B8FD', '#761EEB'], ['#89FCD4', '#2CEBA9']],
          };
          this.empty = false;
        } else {
          this.funnel.values = [];
          this.empty = true;
        }
        return;
      }
      const sanitizedValues = funnelResponse.values.map(group =>
        group.map(value => (isNaN(value) || value == null ? 0 : Number(value)))
      );
      this.funnel = {
        ...this.funnel,
        labels: funnelResponse.labels?.map(label => label || '') || [],
        subLabels: funnelResponse.subLabels?.map(label => label || '') || [],
        colors: funnelResponse.colors || [],
        values: sanitizedValues,
      };
      this.empty = false;
    },

    updateTop5Data(top5Response) {
      if (Array.isArray(top5Response) && top5Response.length > 0) {
        this.top5Data = top5Response;
        const allActivitiesAreNotes = this.top5Data.some((widget) =>
          widget.title === 'As 5 atividades mais executadas' &&
          widget.data.every((activity) => activity.name === 'Nota')
        );
        this.empty = allActivitiesAreNotes;
      } else {
        this.top5Data = [];
        this.empty = true;
      }
    },

    hasDataChanged(newData, oldData) {
      return (
        JSON.stringify(newData.values) !== JSON.stringify(oldData.values) ||
        JSON.stringify(newData.labels) !== JSON.stringify(oldData.labels) ||
        JSON.stringify(newData.subLabels) !== JSON.stringify(oldData.subLabels)
      );
    },

    shouldDisplayDashboard() {
      const hasNonNoteActivities = this.top5Data?.some((widget) =>
        widget.title === 'As 5 atividades mais executadas' &&
        widget.data?.some((activity) => activity.name && activity.name !== 'Nota')
      );

      return !this.empty && this.funnel.values?.length > 0 && hasNonNoteActivities;
    },

    calculateFunnelWidth() {
      Vue.nextTick(() => {
        let element = document.getElementById('funnel');
        if (!element) {
          element = document.getElementById('funnel-card');
          if (!element) {
            return;
          }
        }

        let { width } = element.getBoundingClientRect();
        if (width === 0) {
          width = element.getBoundingClientRect().width * 0.8;
        }

        this.funnel.width = width - 30;
        this.funnel.direction = 'horizontal';
      });
    },

    dataIsEmpty() {
      const hasValidFunnelValues = this.funnel.values?.some((group) =>
        group.some((value) => typeof value === 'number' && !isNaN(value) && value > 0)
      );
      const hasValidRadials = this.data.radials?.some((radial) => typeof radial.value === 'number' && !isNaN(radial.value) && radial.value > 0);
      const hasTop5Widgets = this.top5Data && this.top5Data.length > 0;

      const allActivitiesAreNotes = this.top5Data?.some((widget) =>
        widget.title === 'As 5 atividades mais executadas' &&
        widget.data.every((activity) => activity.name === 'Nota')
      );
      if (!hasValidFunnelValues && !hasValidRadials && (!hasTop5Widgets || allActivitiesAreNotes)) {
        this.empty = true;
        return true;
      }
      this.empty = false;
      return false;
    },

    useFakeData() {
      this.fake = true;

      this.data = {
        labels: [
          this.__('Visitantes'),
          'Leads',
          this.__('Atendidos'),
          this.__('Clientes'),
        ],
        subLabels: [
          'Pago',
          'Org\u00e2nico',
          'Direto',
        ],
        colors: [
          [
            '#FF8EB0',
            '#F31357',
          ],
          [
            '#D5B8FD',
            '#761EEB',
          ],
          [
            '#89FCD4',
            '#2CEBA9',
          ],
        ],
        values: [
          [
            3394,
            752,
            1235,
          ],
          [
            150,
            36,
            29,
          ],
          [
            108,
            33,
            16,
          ],
          [
            21,
            30,
            12,
          ],
        ],
        radials: [
          {
            title: 'ABERTOS',
            value: 13016,
            percent: 30,
            colors: [
              '#761EEB',
              '#D5B8FD',
            ],
          },
          {
            title: 'PERDIDOS',
            value: 25803,
            percent: 60,
            colors: [
              '#B70038',
              '#F31357',
            ],
          },
          {
            title: 'FECHADOS',
            value: 4331,
            percent: 10,
            colors: [
              '#2CEBA9',
              '#89FCD4',
            ],
            large: true,
          },
        ],
      };
    },
  },
};
</script>
